/**
 * Wizard flow — the PURE state model behind the concept-first New Game wizard
 * (platform-rework A3). Three steps, all but the first inside one film:
 *
 *   1. concept  — title + engine + free-text concept + up to 3 reference
 *                 URLs. Advancing CREATES the draft and fires analyzeConcept
 *                 (shell concerns), so the wizard is resumable.
 *   2. analysis — the builder derives a design proposal; its questions, if
 *                 any, are offered inside the film.
 *   3. review   — the server's composed run and first build, followed to the
 *                 working game or the last-resort screen. Nothing to review:
 *                 Create is automatic.
 *
 * No React — unit-tested directly (apps/portal/test/wizardFlow.test.ts).
 * Persistence (createCreation, analyzeConcept) is the shell component's concern.
 */
import type {
  ConceptAnalysisDraft,
  ConceptAnalysisResult,
  ConceptClarificationQuestion,
  EngineRef,
  GameDesign,
  PartialGameSettings,
  ReferenceBrief,
} from '@djgg/creations';
import { sanitizeSettings, type DecisionId, type GameSettings } from '@djgg/creations';
import {
  validateTitle,
  isValidReferenceUrl,
  CONCEPT_MIN_LEN,
  CONCEPT_MAX_LEN,
  MAX_REFERENCE_URLS,
} from '../wizard.js';

export type WizardStepId = 'concept' | 'analysis' | 'review';

/** An analysis request or durable claim is running. Editing the idea now would
 * supersede it, so its provider spend is lost and a second paid run starts. */
export function isAnalysisLive(status: StudioAnalysisState | null): boolean {
  return status === 'claimed' || status === 'following' || status === 'recovering';
}

/** Creator-visible projection of the durable analysis authority. */
export type StudioAnalysisState =
  | 'claimed'
  | 'following'
  | 'recovering'
  | 'clarification-ready'
  | 'proposal-ready'
  | 'stale'
  | 'failed'
  | 'consumed';

/** The film's status line for each analysis state. */
export const ANALYSIS_STATE_TITLE: Record<StudioAnalysisState, string> = {
  claimed: 'Shaping your idea',
  following: 'Your game is already being prepared',
  recovering: 'Picking up where we left off',
  'clarification-ready': 'A few choices for you',
  'proposal-ready': 'Your game plan is ready',
  stale: 'Your idea changed',
  failed: 'We couldn’t shape your idea',
  consumed: 'Your build has started',
};

/** The film's status line while a failed first build is repaired automatically. */
export const FIRST_BUILD_REPAIR_TITLE = 'Making it work';

/** Pure durable-draft projection used by hydration, focus, and tests. */
export function studioAnalysisState(
  draft: ConceptAnalysisDraft,
  options: { now?: number; ownedOperationId?: string } = {},
): StudioAnalysisState {
  if (draft.status === 'claimed') {
    if (draft.leaseExpiresAt !== null && draft.leaseExpiresAt <= (options.now ?? Date.now())) {
      return 'recovering';
    }
    return options.ownedOperationId === draft.operationId ? 'claimed' : 'following';
  }
  if (draft.status === 'clarification') return 'clarification-ready';
  if (draft.status === 'proposal') return 'proposal-ready';
  return draft.status;
}

/** The wizard's pure state. */
export interface WizardFlowState {
  step: WizardStepId;
  title: string;
  engine: EngineRef | null;
  /** The creator's verbatim free-text concept (doubles as the v1 prompt). */
  concept: string;
  /** Fixed MAX_REFERENCE_URLS slots; '' = empty. */
  refUrls: readonly string[];
  /** Set once the draft creation exists (after the concept step). */
  creationId: string | null;
  /** Why analysis stopped, for the last-resort screen ('' = in flight). */
  analysisError: string;
  /** Explicit creator-visible projection of the durable server operation. */
  analysisStatus: StudioAnalysisState | null;
  analysisOperationId: string | null;
  analysisRevision: number | null;
  clarificationQuestions: readonly ConceptClarificationQuestion[];
  clarificationAnswers: Readonly<Record<string, string>>;
  /** A concept keystroke awaiting confirmation because it invalidates answers. */
  pendingConcept: string | null;
  /** The analysis proposal's design: the composed run's input. */
  design: GameDesign | null;
  /** The creator's own player choices from More options, saved on the draft. */
  creatorAnswers: PartialGameSettings;
  referenceBriefs: readonly ReferenceBrief[];
  /** True when a blocked submit was attempted — the step shows its errors. */
  submitAttempted: boolean;
  expectedSettingsRev: number;
}

export const EMPTY_FLOW: WizardFlowState = {
  step: 'concept',
  title: '',
  engine: null,
  concept: '',
  refUrls: Array.from({ length: MAX_REFERENCE_URLS }, () => ''),
  creationId: null,
  analysisError: '',
  analysisStatus: null,
  analysisOperationId: null,
  analysisRevision: null,
  clarificationQuestions: [],
  clarificationAnswers: {},
  pendingConcept: null,
  design: null,
  creatorAnswers: {},
  referenceBriefs: [],
  submitAttempted: false,
  expectedSettingsRev: 0,
};

/** What analyzeConcept returns (wire-shaped; only its design is read). */
export interface AnalysisProposal {
  design: GameDesign;
}

export type WizardFlowAction =
  | { type: 'setTitle'; value: string }
  | { type: 'setEngine'; value: EngineRef | null }
  | { type: 'setConcept'; value: string }
  | { type: 'confirmConceptEdit' }
  | { type: 'cancelConceptEdit' }
  | { type: 'setRefUrl'; index: number; value: string }
  | { type: 'created'; creationId: string }
  | { type: 'discardIdea' }
  | { type: 'beginAnalysis' }
  | {
      type: 'analysisSucceeded';
      proposal?: AnalysisProposal;
      analysis?: ConceptAnalysisResult;
      operationId?: string;
      revision?: number;
      referenceBriefs: readonly ReferenceBrief[];
    }
  | { type: 'analysisReconciled'; draft: ConceptAnalysisDraft; owned: boolean }
  | { type: 'analysisFailed'; message: string }
  | { type: 'answerClarification'; questionId: string; value: string }
  | { type: 'resolveClarification' }
  /** "Keep making my game": the server answers the questions for the creator. */
  | { type: 'skipClarification' }
  | { type: 'answer'; decision: DecisionId; value: GameSettings[DecisionId] }
  | { type: 'showErrors' }
  | { type: 'authoritySaved'; settingsRev: number }
  | {
      type: 'hydrate';
      title: string;
      engine: EngineRef;
      concept: string;
      answers: PartialGameSettings;
      creationId: string;
      gameDesign: GameDesign | null;
      referenceBriefs: readonly ReferenceBrief[];
      analysisDraft?: ConceptAnalysisDraft;
      settingsRev?: number;
    };

export function canResolveClarifications(state: WizardFlowState): boolean {
  return state.clarificationQuestions.every(
    (question) => (state.clarificationAnswers[question.id] ?? '').trim() !== '',
  );
}

/** First error blocking the CURRENT step (empty map = advanceable). */
export function stepErrors(state: WizardFlowState): Partial<Record<string, string>> {
  const errors: Partial<Record<string, string>> = {};
  if (state.step !== 'concept') return errors; // after the concept nothing waits on a submit
  // A title is a useful identity hook, not an entry toll. The browser derives
  // a deterministic editable proposal at submit time when this is blank.
  if (state.title.trim()) Object.assign(errors, validateTitle(state.title));
  if (!state.engine) errors.engine = 'Pick an engine.';
  const concept = state.concept.trim();
  if (concept.length < CONCEPT_MIN_LEN) {
    errors.concept = `Describe your game in at least ${CONCEPT_MIN_LEN} characters — the analysis needs real material.`;
  } else if (concept.length > CONCEPT_MAX_LEN) {
    errors.concept = `Your concept is ${concept.length - CONCEPT_MAX_LEN} characters over the ${CONCEPT_MAX_LEN}-character limit. Shorten it before analyzing; your full text is preserved.`;
  }
  state.refUrls.forEach((url, i) => {
    if (url.trim() !== '' && !isValidReferenceUrl(url)) {
      errors[`refUrl${i}`] = 'Reference links must be https:// URLs.';
    }
  });
  return errors;
}

/** Provider-free title proposal for the first durable draft. It deliberately
 * stays modest: analysis may enrich the game later, while this keeps draft and
 * slug creation deterministic across retries and refreshes. */
export function proposedGameTitle(concept: string): string {
  const stop = new Set(['a', 'an', 'the', 'and', 'or', 'to', 'of', 'in', 'on', 'at', 'with', 'from', 'for',
    'through', 'into', 'your', 'my', 'our', 'their', 'game', 'create', 'make', 'build', 'play', 'where', 'that']);
  const words = concept.normalize('NFKC').match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu) ?? [];
  const picked = words.filter(word => !stop.has(word.toLocaleLowerCase('en-US'))).slice(0, 4);
  const source = picked.length ? picked : words.slice(0, 4);
  const title = source.map(word => word.charAt(0).toLocaleUpperCase('en-US') + word.slice(1)).join(' ').slice(0, 80).trim();
  return title || 'My New Game';
}

/** True when the current step's primary action is allowed. */
export function canAdvance(state: WizardFlowState): boolean {
  return Object.keys(stepErrors(state)).length === 0;
}

/** Statuses a late request or read must never pull back to the interstitial. */
const SETTLED: readonly (StudioAnalysisState | null)[] = ['clarification-ready', 'proposal-ready', 'stale', 'failed', 'consumed'];

export function wizardReducer(state: WizardFlowState, action: WizardFlowAction): WizardFlowState {
  switch (action.type) {
    case 'discardIdea':
      return state.creationId ? state : { ...EMPTY_FLOW, engine: state.engine };
    case 'setTitle':
      return { ...state, title: action.value };
    case 'setEngine':
      return { ...state, engine: action.value };
    case 'setConcept':
      if (
        action.value !== state.concept &&
        Object.keys(state.clarificationAnswers).length > 0 &&
        state.pendingConcept === null
      ) {
        return { ...state, pendingConcept: action.value };
      }
      return { ...state, concept: action.value };
    case 'confirmConceptEdit':
      return state.pendingConcept === null
        ? state
        : {
            ...state,
            concept: state.pendingConcept,
            pendingConcept: null,
            clarificationAnswers: {},
            clarificationQuestions: [],
            analysisOperationId: null,
            analysisRevision: null,
            analysisStatus: 'stale',
          };
    case 'cancelConceptEdit':
      return { ...state, pendingConcept: null };
    case 'setRefUrl':
      return { ...state, refUrls: state.refUrls.map((u, i) => (i === action.index ? action.value : u)) };
    case 'created':
      return { ...state, creationId: action.creationId, expectedSettingsRev: 1 };
    case 'beginAnalysis':
      if (!canAdvance(state)) return { ...state, submitAttempted: true };
      return { ...state, step: 'analysis', analysisError: '', analysisStatus: 'claimed', submitAttempted: false };
    case 'analysisSucceeded': {
      const result = action.analysis;
      const identity = {
        analysisOperationId: action.operationId ?? state.analysisOperationId,
        analysisRevision: action.revision ?? state.analysisRevision,
        referenceBriefs: action.referenceBriefs,
      };
      if (result?.kind === 'failure') {
        return { ...state, ...identity, step: 'analysis', analysisStatus: 'failed',
          analysisError: result.validationErrors.join(' '), clarificationQuestions: [] };
      }
      if (result?.kind === 'clarification') {
        return { ...state, ...identity, step: 'analysis', analysisError: '', analysisStatus: 'clarification-ready',
          clarificationQuestions: result.questions, design: 'draftProposal' in result ? result.draftProposal.design : null, submitAttempted: false };
      }
      const proposal = result?.kind === 'proposal' ? result.proposal : action.proposal;
      if (!proposal) return { ...state, analysisStatus: 'failed', analysisError: 'Analysis returned no proposal.' };
      return { ...state, ...identity, step: 'review', analysisError: '', analysisStatus: 'proposal-ready',
        clarificationQuestions: [], submitAttempted: false, design: proposal.design };
    }
    case 'analysisReconciled': {
      const draft = action.draft;
      if (state.analysisRevision !== null && draft.revision < state.analysisRevision) return state;
      if (
        draft.status !== 'consumed' &&
        state.analysisRevision !== null &&
        draft.revision <= state.analysisRevision &&
        (state.step !== 'analysis' || SETTLED.includes(state.analysisStatus))
      ) return state;
      if (draft.status === 'claimed') {
        // A delayed read from an old request must never pull a completed result
        // back to the interstitial. While pending, the durable claim is more
        // authoritative than the lifetime of any one browser request.
        if (state.step !== 'analysis' || SETTLED.includes(state.analysisStatus)) return state;
        return {
          ...state,
          analysisError: '',
          analysisStatus: studioAnalysisState(draft, { ownedOperationId: action.owned ? draft.operationId : undefined }),
          analysisOperationId: draft.operationId,
          analysisRevision: draft.revision,
          referenceBriefs: draft.referenceBriefs ?? state.referenceBriefs,
        };
      }
      if (draft.status === 'stale' || (draft.status === 'failed' && draft.result?.kind !== 'failure')) {
        return {
          ...state,
          step: 'analysis',
          analysisStatus: draft.status,
          analysisError: draft.failure?.message
            ?? (draft.status === 'stale' ? 'The saved idea changed before analysis completed.' : 'Analysis failed.'),
          analysisOperationId: draft.operationId,
          analysisRevision: draft.revision,
          referenceBriefs: draft.referenceBriefs ?? state.referenceBriefs,
        };
      }
      if (draft.result) {
        const reconciled = wizardReducer(state, {
          type: 'analysisSucceeded',
          analysis: draft.result,
          operationId: draft.operationId,
          revision: draft.revision,
          referenceBriefs: draft.referenceBriefs ?? state.referenceBriefs,
        });
        return draft.status === 'consumed' ? { ...reconciled, analysisStatus: 'consumed' } : reconciled;
      }
      return { ...state, analysisStatus: studioAnalysisState(draft), analysisOperationId: draft.operationId,
        analysisRevision: draft.revision };
    }
    case 'analysisFailed':
      // A request can reject after its durable result has already arrived via
      // reconciliation. Transport lifetime must not overwrite saved success.
      if (state.step !== 'analysis' || SETTLED.includes(state.analysisStatus)) return state;
      return {
        ...state,
        analysisError: action.message || 'Analysis failed.',
        analysisStatus: state.clarificationQuestions.length > 0 ? 'clarification-ready' : 'failed',
      };
    case 'answerClarification':
      return { ...state, clarificationAnswers: { ...state.clarificationAnswers, [action.questionId]: action.value } };
    case 'resolveClarification':
      return canResolveClarifications(state)
        ? { ...state, analysisStatus: 'recovering', analysisError: '' }
        : { ...state, submitAttempted: true };
    case 'skipClarification':
      return state.analysisStatus === 'clarification-ready' ? { ...state, analysisStatus: 'recovering', analysisError: '' } : state;
    case 'answer':
      return { ...state, creatorAnswers: sanitizeSettings({ ...state.creatorAnswers, [action.decision]: action.value }).values };
    case 'showErrors':
      return { ...state, submitAttempted: true };
    case 'authoritySaved':
      return { ...state, expectedSettingsRev: action.settingsRev };
    case 'hydrate': {
      const base: WizardFlowState = {
        ...EMPTY_FLOW,
        title: action.title,
        engine: action.engine,
        concept: action.concept,
        // Surface the previously-analyzed reference URLs for a re-run.
        refUrls: Array.from({ length: MAX_REFERENCE_URLS }, (_, i) => action.referenceBriefs[i]?.url ?? ''),
        creationId: action.creationId,
        creatorAnswers: sanitizeSettings(action.answers).values,
        referenceBriefs: action.referenceBriefs,
        expectedSettingsRev: action.settingsRev ?? 0,
      };
      const draft = action.analysisDraft;
      if (!draft) return base; // No analysis yet — land on the concept step.
      const identity = { analysisOperationId: draft.operationId, analysisRevision: draft.revision };
      if (draft.status === 'clarification' && draft.result?.kind === 'clarification') {
        return { ...base, ...identity, step: 'analysis', analysisStatus: 'clarification-ready',
          clarificationQuestions: draft.result.questions, clarificationAnswers: draft.partialAnswers,
          design: 'draftProposal' in draft.result ? draft.result.draftProposal.design : null };
      }
      if (draft.status === 'failed' && draft.result?.kind === 'failure') {
        return { ...base, ...identity, step: 'analysis', analysisStatus: 'failed',
          analysisError: draft.result.validationErrors.join(' ') };
      }
      if (draft.status === 'proposal' && draft.result?.kind === 'proposal') {
        return { ...base, ...identity, step: 'review', analysisStatus: 'proposal-ready', design: draft.result.proposal.design };
      }
      if (draft.status === 'consumed') {
        // The first build has started: the page follows it in the same film.
        return { ...base, ...identity, step: 'review', analysisStatus: 'consumed', design: action.gameDesign };
      }
      return { ...base, ...identity, step: 'analysis', analysisStatus: studioAnalysisState(draft),
        clarificationAnswers: draft.partialAnswers, analysisError: draft.failure?.message ?? '' };
    }
  }
}
